import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Activity,
  Check,
  CircleAlert,
  Eye,
  FileText,
  Home,
  Info,
  Link as LinkIcon,
  List,
  Rocket,
  Shield,
  X,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { Spinner } from '@/components/ui/spinner';
import { useEngagementQuery } from '@/lib/api/engagements';
import {
  rollupColorToPostureStatus,
  useOverallPostureQuery,
  type OverallPostureDomain,
} from '@/lib/api/health-posture';
import { useRegisterListQuery, type RegisterEntry } from '@/lib/api/registers';
import { useSnapshotListQuery, type SnapshotListItem } from '@/lib/api/snapshots';
import type { ClientViewerGrant } from '@/lib/api/settings';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';

/**
 * Viewer-preview modal (T-0110 skeleton, T-0116 extension).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-soft-gaps.js
 * → window.Modals.openViewerPreview`. Two-pane layout — a "simulated client
 * portal" frame on the left + a "hidden from viewer / visible scope"
 * sidebar on the right.
 *
 * **RLS approach (unchanged from T-0110)** — the modal opens against the
 * firm-admin's auth, NOT a faked viewer session. Why:
 *   1. The grant's RLS context (`engagement_id`) is the same context the
 *      firm-admin already holds on this engagement — every endpoint the
 *      modal calls (engagement detail, overall posture, snapshots,
 *      registers) already enforces engagement-scoped RLS server-side per
 *      ADR-008.
 *   2. The viewer's *additional* constraints (no drafts, no audit, no AI
 *      proposals, no chat, snapshots only) are layered on top of the RLS
 *      base; we surface that constraint client-side by filtering to the
 *      reads a real viewer session is allowed to do.
 *   3. The grant's `project_id` (nullable, NULL = whole client) determines
 *      which modules the viewer would see; we filter the visible module
 *      list against the engagement's `modules_enabled` array to mirror
 *      that scope.
 *
 * **T-0116 extension** — the modal now renders real bodies for every
 * module a viewer can see:
 *   - **Overview**: engagement posture + recent snapshots + the per-
 *     domain posture grid (read from `useOverallPostureQuery → per_domain`).
 *   - **Registers**: a scoped table of register entries. Loads
 *     `contract_compliance_baseline` by default — the closest "default
 *     register" a viewer would land on per the documents-IA. AI / draft /
 *     proposed rows are filtered client-side; the rows the table renders
 *     are the snapshot-projected set a viewer would receive.
 *   - **Contract / Health / Go-Live**: the module tabs are shown when
 *     `modules_enabled` includes them; each opens a register subset
 *     scoped to the module (deliverables for Contract, health_findings
 *     for Health, readiness_score for Go-Live).
 *
 * The "Hidden from viewer" sidebar list is preserved verbatim per the
 * post-mortem in `docs/build-process/2026-06-02-design-handoff-miss.md`
 * — the auditability story depends on the exact list.
 */

export interface ViewerPreviewModalProps {
  viewer: ClientViewerGrant | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Wires the "Re-issue magic link" sidebar button. */
  onReissue?: (viewer: ClientViewerGrant) => void;
}

type ModuleId =
  | 'overview'
  | 'contract_compliance'
  | 'health_check'
  | 'go_live_readiness'
  | 'register';

interface ModuleInfo {
  id: ModuleId;
  label: string;
  icon: LucideIcon;
}

const MODULE_REGISTRY: ReadonlyArray<ModuleInfo> = [
  { id: 'overview', label: 'Overview', icon: Home },
  { id: 'contract_compliance', label: 'Contract', icon: Shield },
  { id: 'health_check', label: 'Health Check', icon: Activity },
  { id: 'go_live_readiness', label: 'Go-Live Readiness', icon: Rocket },
  { id: 'register', label: 'Registers', icon: List },
];

/**
 * What the viewer cannot see — surfaced verbatim from the prototype's
 * `hiddenList` so the auditability claim ("here's exactly what's hidden")
 * stays exact. The implementer's note in the ticket §7 calls out: the
 * preview is the auditability tell.
 */
const HIDDEN_FROM_VIEWER: ReadonlyArray<string> = [
  'AI proposal queue & confidence chips',
  'Audit trail',
  'Drafts and in-progress register edits',
  'Internal-team chat',
  'Promote-back queue and methodology library',
  'Per-call AI cost telemetry',
  "Snapshots after the viewer's scope expiry date",
];

/**
 * Per-module register binding — the register_type a viewer would land on
 * when they click that module tab in the simulated portal. Sourced from
 * the contract IA constants in `apps/web/src/components/registers/filter-bar.tsx`
 * and the per-tab register types used by the engagement screens.
 */
const MODULE_REGISTER_TYPE: Record<ModuleId, string> = {
  overview: 'contract_compliance_baseline',
  register: 'contract_compliance_baseline',
  contract_compliance: 'deliverables',
  health_check: 'health_findings',
  go_live_readiness: 'readiness_score',
};

export function ViewerPreviewModal({
  viewer,
  open,
  onOpenChange,
  onReissue,
}: ViewerPreviewModalProps) {
  const engagementId = viewer?.engagement_id;
  const enabled = open && Boolean(engagementId);

  const engQuery = useEngagementQuery(enabled ? engagementId : undefined);
  const postureQuery = useOverallPostureQuery(enabled ? engagementId : undefined);
  const snapshotsQuery = useSnapshotListQuery(enabled ? engagementId : undefined);

  // Filter the visible module list against (a) what the engagement has
  // enabled and (b) what the grant's project_id allows. Whole-client
  // grants (project_id === null) see every enabled module; project-scoped
  // grants see only the modules enabled on their project (the engagement
  // is the project's engagement, so this is the same array).
  const visibleModules: ModuleInfo[] = React.useMemo(() => {
    const moduleArray = engQuery.data?.modules_enabled ?? [];
    const enabledSet = new Set(moduleArray);
    return MODULE_REGISTRY.filter(
      (m) => m.id === 'overview' || m.id === 'register' || enabledSet.has(m.id),
    );
  }, [engQuery.data?.modules_enabled]);

  // Active module tab. Reset to overview on every open / new viewer.
  const [activeModuleId, setActiveModuleId] = React.useState<ModuleId>('overview');
  React.useEffect(() => {
    if (open) setActiveModuleId('overview');
  }, [open, viewer?.grant_id]);

  const activeModule =
    visibleModules.find((m) => m.id === activeModuleId) ?? visibleModules[0] ?? MODULE_REGISTRY[0]!;

  const posture: PostureStatus = postureQuery.data
    ? rollupColorToPostureStatus(postureQuery.data.overall_rollup)
    : 'unassessed';

  const titleId = viewer ? `viewer-preview-title-${viewer.grant_id}` : 'viewer-preview-title';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          aria-describedby={undefined}
          className={cn(
            // 1180px max width matches the prototype's modal-width.
            'fixed left-1/2 top-1/2 z-50 grid w-[min(1180px,96vw)] max-h-[92vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <Header viewer={viewer} titleId={titleId} />
          <div className="grid min-h-0 grid-cols-1 overflow-hidden lg:grid-cols-[1fr_280px]">
            <PortalFrame
              viewer={viewer}
              modules={visibleModules}
              activeModuleId={activeModule.id}
              onActiveModuleChange={setActiveModuleId}
              posture={posture}
              perDomain={postureQuery.data?.per_domain ?? []}
              clientName={engQuery.data?.client_name ?? viewer?.client_name ?? ''}
              projectName={engQuery.data?.project_name ?? viewer?.project_name ?? ''}
              loading={engQuery.isLoading || postureQuery.isLoading}
              snapshots={snapshotsQuery.data?.items ?? []}
              snapshotsLoading={snapshotsQuery.isLoading}
              engagementId={engagementId}
            />
            <Sidebar viewer={viewer} visibleModules={visibleModules} onReissue={onReissue} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Header({ viewer, titleId }: { viewer: ClientViewerGrant | null; titleId: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-6 py-4">
      <div className="min-w-0">
        <Dialog.Title id={titleId} className="text-h3 font-semibold text-ink-900">
          Client viewer preview {viewer ? `— what ${viewer.viewer_name} sees` : ''}
        </Dialog.Title>
        {viewer ? (
          <p className="mt-1 text-meta text-ink-500">
            {viewer.viewer_email} · {viewer.client_name}
            {viewer.project_name ? ` · ${viewer.project_name}` : ' · whole client'} · auth{' '}
            <span className="font-mono">{viewer.auth_mode}</span>
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <span className="inline-flex h-[22px] items-center rounded-full bg-ai-surface px-2.5 text-meta font-medium text-ai-ink">
          Read-only simulation
        </span>
        <Dialog.Close
          aria-label="Close viewer preview"
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-card',
            'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
          )}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Dialog.Close>
      </div>
    </div>
  );
}

function PortalFrame({
  viewer,
  modules,
  activeModuleId,
  onActiveModuleChange,
  posture,
  perDomain,
  clientName,
  projectName,
  loading,
  snapshots,
  snapshotsLoading,
  engagementId,
}: {
  viewer: ClientViewerGrant | null;
  modules: ReadonlyArray<ModuleInfo>;
  activeModuleId: ModuleId;
  onActiveModuleChange: (id: ModuleId) => void;
  posture: PostureStatus;
  perDomain: ReadonlyArray<OverallPostureDomain>;
  clientName: string;
  projectName: string;
  loading: boolean;
  snapshots: ReadonlyArray<SnapshotListItem>;
  snapshotsLoading: boolean;
  engagementId: string | undefined;
}) {
  const initials = viewer
    ? viewer.viewer_name
        .split(' ')
        .map((part) => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?';
  const activeModule = modules.find((m) => m.id === activeModuleId) ?? modules[0];
  if (!activeModule) return null;

  return (
    <section className="min-w-0 overflow-y-auto bg-paper-100 px-6 py-5">
      <div className="overflow-hidden rounded-large border border-ink-100 bg-paper-0 shadow-sm">
        {/* Topbar — matches the prototype's `.vp-topbar` */}
        <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-card bg-ink-900 text-paper-0">
              <span className="font-serif text-h3 font-semibold">A</span>
            </span>
            <div className="flex flex-col">
              <span className="text-body-sm font-semibold text-ink-900">Avero · Caliber</span>
              <span className="text-meta text-ink-500">Client portal</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-meta text-ink-500">
              {viewer?.viewer_name ?? '—'} · {clientName}
            </span>
            <span
              aria-hidden="true"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-paper-100 text-meta font-semibold text-ink-700"
            >
              {initials}
            </span>
          </div>
        </header>

        {/* Two-pane portal mock */}
        <div className="grid grid-cols-[160px_1fr]">
          <aside className="border-r border-ink-100 bg-paper-50 p-3">
            <p className="px-2 text-[10px] font-semibold uppercase tracking-wider text-ink-500">
              Engagement
            </p>
            <p className="mt-1 px-2 text-body-sm font-semibold leading-tight text-ink-900">
              {projectName || '—'}
            </p>
            <p className="mt-0.5 px-2 text-meta text-ink-500">{clientName || '—'}</p>
            <nav className="mt-3 flex flex-col gap-0.5" role="tablist" aria-label="Modules">
              {modules.map((m) => {
                const Icon = m.icon;
                const active = m.id === activeModuleId;
                return (
                  <button
                    key={m.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => onActiveModuleChange(m.id)}
                    className={cn(
                      'flex items-center gap-2 rounded-card px-2 py-1.5 text-left text-meta',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                      active
                        ? 'bg-paper-0 font-semibold text-ink-900 shadow-sm'
                        : 'text-ink-700 hover:bg-paper-0',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
                    {m.label}
                  </button>
                );
              })}
            </nav>
            <p className="mt-4 border-t border-ink-100 px-2 pt-3 text-[10px] uppercase tracking-wider text-ink-500">
              Access expires
            </p>
            <p
              className="mt-0.5 px-2 font-mono text-meta text-ink-700"
              title="Schema gap: no explicit expiry column today"
            >
              N/A
            </p>
          </aside>
          <div className="flex flex-col">
            <div className="flex items-start justify-between gap-3 border-b border-ink-100 px-5 py-3">
              <div>
                <p className="text-h3 font-semibold text-ink-900">{activeModule.label}</p>
                <p className="text-meta text-ink-500">
                  Latest snapshot ·{' '}
                  {snapshots[0]
                    ? new Date(snapshots[0].captured_at).toISOString().slice(0, 10)
                    : 'none captured yet'}
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 gap-1 px-2 text-meta"
                disabled
              >
                <FileText className="h-3 w-3" aria-hidden="true" />
                Download PDF
              </Button>
            </div>
            <ModuleBody
              moduleId={activeModule.id}
              engagementId={engagementId}
              viewer={viewer}
              posture={posture}
              perDomain={perDomain}
              loading={loading}
              snapshots={snapshots}
              snapshotsLoading={snapshotsLoading}
            />
            <footer className="mt-auto flex items-center justify-between border-t border-ink-100 bg-paper-50 px-5 py-2.5 text-meta text-ink-500">
              <span className="inline-flex items-center gap-1.5">
                <Shield className="h-3 w-3" aria-hidden="true" />
                Snapshot view · changes since this snapshot are not visible
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Info className="h-3 w-3" aria-hidden="true" />
                Viewer sessions are audit-logged
              </span>
            </footer>
          </div>
        </div>
      </div>
    </section>
  );
}

function ModuleBody({
  moduleId,
  engagementId,
  viewer,
  posture,
  perDomain,
  loading,
  snapshots,
  snapshotsLoading,
}: {
  moduleId: ModuleId;
  engagementId: string | undefined;
  viewer: ClientViewerGrant | null;
  posture: PostureStatus;
  perDomain: ReadonlyArray<OverallPostureDomain>;
  loading: boolean;
  snapshots: ReadonlyArray<SnapshotListItem>;
  snapshotsLoading: boolean;
}) {
  if (moduleId === 'overview') {
    return (
      <div className="flex flex-col gap-3 p-5">
        {loading ? (
          <div className="flex items-center gap-2 text-meta text-ink-500">
            <Spinner />
            Loading scoped engagement data…
          </div>
        ) : (
          <>
            <div className="rounded-card border border-ink-100 bg-paper-50 p-4">
              <p className="text-meta uppercase tracking-wider text-ink-500">Engagement posture</p>
              <div className="mt-2 flex items-center gap-2">
                <PostureChip status={posture} dot />
                <span className="text-body-sm text-ink-700">As of the latest snapshot</span>
              </div>
            </div>
            <PostureGrid perDomain={perDomain} />
            <SnapshotList
              snapshots={snapshots}
              loading={snapshotsLoading}
              viewerFirstName={viewer?.viewer_name.split(' ')[0]}
            />
            <RlsCallout />
          </>
        )}
      </div>
    );
  }
  const registerType = MODULE_REGISTER_TYPE[moduleId];
  return (
    <div className="flex flex-col gap-3 p-5">
      <ScopedRegisterGrid
        engagementId={engagementId}
        registerType={registerType}
        moduleLabel={MODULE_REGISTRY.find((m) => m.id === moduleId)?.label ?? 'Module'}
      />
      <RlsCallout />
    </div>
  );
}

function PostureGrid({ perDomain }: { perDomain: ReadonlyArray<OverallPostureDomain> }) {
  if (perDomain.length === 0) return null;
  return (
    <div className="rounded-card border border-ink-100 bg-paper-50 p-4">
      <p className="text-meta uppercase tracking-wider text-ink-500">Domain posture</p>
      <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {perDomain.map((d) => {
          const status = rollupColorToPostureStatus(d.latest_rollup_color);
          return (
            <li
              key={d.domain_id}
              className="flex items-center justify-between gap-2 rounded-card border border-ink-100 bg-paper-0 px-3 py-2"
            >
              <span className="truncate text-body-sm text-ink-700">{d.name}</span>
              <PostureChip status={status} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SnapshotList({
  snapshots,
  loading,
  viewerFirstName,
}: {
  snapshots: ReadonlyArray<SnapshotListItem>;
  loading: boolean;
  viewerFirstName: string | undefined;
}) {
  return (
    <div className="rounded-card border border-ink-100 bg-paper-50 p-4">
      <p className="text-meta uppercase tracking-wider text-ink-500">
        Recent snapshots available to {viewerFirstName ?? 'viewer'}
      </p>
      {loading ? (
        <div className="mt-2 flex items-center gap-2 text-meta text-ink-500">
          <Spinner />
          Loading snapshots…
        </div>
      ) : snapshots.length === 0 ? (
        <p className="mt-2 text-meta text-ink-500">
          No snapshots captured on this engagement yet. The viewer would see an empty portal until
          the engagement team captures one.
        </p>
      ) : (
        <ul className="mt-2 flex flex-col divide-y divide-ink-100">
          {snapshots.slice(0, 5).map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-2 py-2 text-body-sm">
              <span className="font-medium text-ink-900">
                {s.snapshot_type === 'pulse'
                  ? 'Pulse Report'
                  : s.snapshot_type === 'posture'
                    ? 'Posture Snapshot'
                    : 'Readiness Report'}
              </span>
              <span className="font-mono text-meta text-ink-500">{formatDate(s.captured_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ScopedRegisterGrid({
  engagementId,
  registerType,
  moduleLabel,
}: {
  engagementId: string | undefined;
  registerType: string;
  moduleLabel: string;
}) {
  // Fetch the live register against the firm-admin's auth. The endpoint
  // enforces RLS server-side; we then filter client-side to the viewer-
  // visible subset: status !== 'proposed' (no AI proposals), origin is
  // not the un-confirmed-AI flag, and we cap at the top N rows for the
  // simulated portal layout.
  const list = useRegisterListQuery(engagementId, registerType, { limit: 30 });
  const entries = list.data?.entries ?? [];

  // Client-side viewer filter — mirrors the policy a viewer's grant
  // would enforce server-side once the viewer-scope read paths land.
  // Drops in-flight drafts + AI-proposed rows so the simulated portal
  // shows only what the snapshot would project.
  const viewerVisible = React.useMemo(
    () => entries.filter((e) => e.status !== 'proposed' && e.status !== 'draft'),
    [entries],
  );

  return (
    <div className="rounded-card border border-ink-100 bg-paper-50 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-meta uppercase tracking-wider text-ink-500">{moduleLabel} register</p>
        <span className="font-mono text-meta text-ink-500">{registerType}</span>
      </div>
      {list.isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-meta text-ink-500">
          <Spinner />
          Loading scoped register…
        </div>
      ) : list.isError ? (
        <p className="mt-3 text-meta text-cite-broken">
          Could not load the {moduleLabel.toLowerCase()} register. The viewer would see an empty
          state here.
        </p>
      ) : viewerVisible.length === 0 ? (
        <p className="mt-3 text-meta text-ink-500">
          The viewer would see no rows in this register — either none exist yet, or every row is in
          a draft / AI-proposed state and filtered out per the viewer scope policy.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-ink-100 overflow-hidden rounded-card border border-ink-100 bg-paper-0">
          {viewerVisible.slice(0, 6).map((row) => (
            <li key={row.id} className="px-3 py-2 text-body-sm">
              <RegisterRowSummary row={row} />
            </li>
          ))}
        </ul>
      )}
      {entries.length > viewerVisible.length ? (
        <p className="mt-2 text-meta text-ink-500">
          {entries.length - viewerVisible.length} draft / AI-proposed rows filtered out — not shown
          to the viewer.
        </p>
      ) : null}
    </div>
  );
}

function RegisterRowSummary({ row }: { row: RegisterEntry }) {
  // Pull a display-friendly title from the JSONB payload. We accept
  // any of `title`, `name`, `subject`, `description` — the prototype
  // and the framework_definitions vary.
  const payload = (row.payload ?? {}) as Record<string, unknown>;
  const title =
    pickString(payload, 'title') ??
    pickString(payload, 'name') ??
    pickString(payload, 'subject') ??
    pickString(payload, 'description') ??
    row.id.slice(0, 8);
  const status = row.status;
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate font-medium text-ink-900" title={title}>
          {title}
        </p>
        <p className="mt-0.5 font-mono text-[10px] text-ink-500">{row.id}</p>
      </div>
      <span className="rounded-chip bg-ink-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-ink-700">
        {status}
      </span>
    </div>
  );
}

function RlsCallout() {
  return (
    <div className="flex items-start gap-2 rounded-card border border-ai-surface-border bg-ai-surface p-3 text-meta text-ai-ink">
      <Info className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden="true" />
      <p className="leading-[18px]">
        The viewer only sees data captured into snapshots. Drafts, AI proposals, the audit trail,
        and internal-team chat are withheld by the server for every viewer. This preview shows only
        what a real viewer is allowed to read.
      </p>
    </div>
  );
}

function Sidebar({
  viewer,
  visibleModules,
  onReissue,
}: {
  viewer: ClientViewerGrant | null;
  visibleModules: ReadonlyArray<ModuleInfo>;
  onReissue?: (viewer: ClientViewerGrant) => void;
}) {
  return (
    <aside className="flex flex-col gap-4 overflow-y-auto border-t border-ink-100 bg-paper-100 px-5 py-5 lg:border-l lg:border-t-0">
      <section>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-700">
          Hidden from {viewer?.viewer_name.split(' ')[0] ?? 'viewer'}
        </p>
        <ul className="mt-2 flex flex-col gap-0">
          {HIDDEN_FROM_VIEWER.map((h) => (
            <li
              key={h}
              className="flex items-start gap-2 border-b border-dashed border-ink-200 py-1.5 text-meta text-ink-700"
            >
              <CircleAlert
                className="mt-0.5 h-3 w-3 flex-none text-posture-critical"
                aria-hidden="true"
              />
              <span>{h}</span>
            </li>
          ))}
        </ul>
      </section>
      <section>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-700">
          Visible scope
        </p>
        <ul className="mt-2 flex flex-col gap-0">
          {visibleModules.map((m) => (
            <li
              key={m.id}
              className="flex items-start gap-2 border-b border-dashed border-ink-200 py-1.5 text-meta text-ink-700"
            >
              <Check className="mt-0.5 h-3 w-3 flex-none text-posture-healthy" aria-hidden="true" />
              <span>{m.label}</span>
            </li>
          ))}
          {visibleModules.length === 0 ? (
            <li className="py-1.5 text-meta text-ink-500">
              No modules enabled — the viewer would see an empty portal.
            </li>
          ) : null}
        </ul>
      </section>
      {viewer?.auth_mode === 'magic_link' ? (
        <div className="flex items-start gap-2 rounded-card border border-ai-surface-border bg-ai-surface p-3 text-meta text-ai-ink">
          <Eye className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden="true" />
          <p className="leading-[18px]">
            <strong>Magic-link auth.</strong> {viewer.viewer_name.split(' ')[0]} signs in via a
            single-use link sent to <span className="font-mono">{viewer.viewer_email}</span>. Links
            expire 15 minutes after they are sent.
          </p>
        </div>
      ) : null}
      {onReissue && viewer && viewer.auth_mode === 'magic_link' && viewer.status !== 'revoked' ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2"
          onClick={() => onReissue(viewer)}
        >
          <LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />
          Re-issue magic link
        </Button>
      ) : null}
    </aside>
  );
}

function pickString(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key];
  if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  return null;
}
